import React, { useState } from 'react';
import { Loader2, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { oapi, uploadOfficialPhoto } from '@/lib/officialsApi';
import { OfficialAvatar } from '../StatusPill';

const ProfileCard = ({ r, onSaved }) => {
  const [phone, setPhone] = useState(r.phone || '');
  const [photo, setPhoto] = useState(r.photo_path || '');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try { setPhoto(await uploadOfficialPhoto(file)); } catch (er) { toast.error(er.message); } finally { setUploading(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      await oapi(`/officials/portal/profile/${r.id}`, { method: 'PUT', body: { phone, photo_path: photo } });
      toast.success(`Profile saved for ${r.org_name}`);
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border rounded-2xl p-5 space-y-4" data-testid={`portal-profile-${r.id}`}>
      <div className="flex items-center gap-3">
        <OfficialAvatar name={r.full_name} photo={photo} size="w-14 h-14" />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-bold uppercase tracking-wide text-[#CF142B]">{r.org_name}</div>
          <div className="font-bold text-slate-900 truncate">{r.full_name}</div>
          <div className="text-xs text-slate-500 truncate">{r.email}</div>
        </div>
        <label className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#00247D] cursor-pointer hover:underline" data-testid={`portal-photo-upload-${r.id}`}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}Photo
          <input type="file" accept="image/*" className="hidden" onChange={onFile} />
        </label>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Phone</Label>
        <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 345 555 0100" data-testid={`portal-phone-${r.id}`} />
      </div>
      <div className="text-xs text-slate-500">
        {r.sports?.length > 0 && <div>Sports: <span className="text-slate-700 capitalize">{r.sports.join(', ')}</span></div>}
        {r.certifications?.length > 0 && <div>Certifications: <span className="text-slate-700">{r.certifications.join(', ')}</span></div>}
        <div className="mt-1">Your league manages your sports, positions and certifications.</div>
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={busy || uploading} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid={`portal-profile-save-${r.id}`}>
          {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Save
        </Button>
      </div>
    </div>
  );
};

export const PortalProfile = ({ records, reload }) => (
  <div className="grid md:grid-cols-2 gap-4" data-testid="portal-profile">
    {records.map(r => <ProfileCard key={r.id} r={r} onSaved={reload} />)}
  </div>
);
